import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { isHttpUrl, isValidDomain } from "@/lib/store";
import type { DomainItem, DomainStatus, RedirectType } from "@/types";

interface DomainFormModalProps {
  open: boolean;
  editing: DomainItem | null;
  defaults: { redirectType: RedirectType; status: DomainStatus };
  onClose: () => void;
  onSubmit: (payload: Omit<DomainItem, "id" | "clicks" | "createdAt">, id?: string) => Promise<string | null>;
}

const EMPTY = {
  domain: "",
  targetUrl: "",
  redirectType: "302" as RedirectType,
  status: "active" as DomainStatus,
  remark: "",
};

const TYPE_OPTIONS: { value: RedirectType; label: string; desc: string }[] = [
  { value: "301", label: "301 永久跳转", desc: "搜索引擎会更新索引，适合长期方案" },
  { value: "302", label: "302 临时跳转", desc: "不改变索引，适合活动 / 临时分发" },
  { value: "mask", label: "隐藏跳转", desc: "地址栏保持中转域名不变" },
];

export default function DomainFormModal({ open, editing, defaults, onClose, onSubmit }: DomainFormModalProps) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setForm(
        editing
          ? {
              domain: editing.domain,
              targetUrl: editing.targetUrl,
              redirectType: editing.redirectType,
              status: editing.status,
              remark: editing.remark,
            }
          : { ...EMPTY, redirectType: defaults.redirectType, status: defaults.status },
      );
    }
  }, [open, editing, defaults.redirectType, defaults.status]);

  const handleSave = () => {
    void save();
  };

  const save = async () => {
    if (!isValidDomain(form.domain)) {
      setError("请填写有效域名，例如 go.example.com");
      return;
    }
    if (!isHttpUrl(form.targetUrl)) {
      setError("目标网址需以 http:// 或 https:// 开头");
      return;
    }
    const message = await onSubmit(
      {
        domain: form.domain.trim(),
        targetUrl: form.targetUrl.trim(),
        redirectType: form.redirectType,
        status: form.status,
        remark: form.remark.trim(),
      },
      editing?.id,
    );
    if (message) {
      setError(message);
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "编辑域名跳转" : "新增域名跳转"}
      subtitle={editing ? "修改现有域名的跳转规则" : "为你的中转域名设置目标网址"}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            {editing ? "保存修改" : "确认新增"}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="font-label text-sm font-medium text-foreground-800">中转域名</span>
          <input
            type="text"
            value={form.domain}
            onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
            placeholder="例如 go.yourbrand.com"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-500"
          />
        </label>

        <label className="flex flex-col gap-2">
          <span className="font-label text-sm font-medium text-foreground-800">跳转到的目标网址</span>
          <input
            type="text"
            value={form.targetUrl}
            onChange={(e) => setForm((f) => ({ ...f, targetUrl: e.target.value }))}
            placeholder="https://你的目标页面地址"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-500"
          />
        </label>

        <div className="flex flex-col gap-2">
          <span className="font-label text-sm font-medium text-foreground-800">跳转方式</span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {TYPE_OPTIONS.map((opt) => {
              const selected = form.redirectType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, redirectType: opt.value }))}
                  className={`rounded-md border px-3 py-2.5 text-left transition-colors cursor-pointer ${
                    selected
                      ? "border-primary-500 bg-primary-100"
                      : "border-background-300 bg-background-50 hover:border-primary-300"
                  }`}
                >
                  <span className="block text-xs font-medium text-foreground-900">{opt.label}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-foreground-500">
            {TYPE_OPTIONS.find((o) => o.value === form.redirectType)?.desc}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-label text-sm font-medium text-foreground-800">运行状态</span>
          <div className="inline-flex w-fit gap-1 rounded-full bg-background-100 p-1">
            {[
              { value: "active" as DomainStatus, label: "运行中" },
              { value: "paused" as DomainStatus, label: "暂停" },
            ].map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setForm((f) => ({ ...f, status: s.value }))}
                className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer ${
                  form.status === s.value ? "bg-primary-500 font-medium text-background-50" : "text-foreground-600"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <label className="flex flex-col gap-2">
          <span className="font-label text-sm font-medium text-foreground-800">备注（选填）</span>
          <input
            type="text"
            value={form.remark}
            onChange={(e) => setForm((f) => ({ ...f, remark: e.target.value }))}
            placeholder="例如：夏季大促落地页"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-500"
          />
        </label>

        {error ? (
          <div className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
            <i className="ri-error-warning-line" />
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}